/**
 * useMerchantMemberActions Hook - Coordinator Pattern Implementation
 *
 * This hook coordinates merchant member-related actions.
 *
 * Pattern: COORDINATOR
 * - Orchestrates member mutations
 * - Returns Promises (no data storage)
 * - Loading states per action
 * - No computed values or aggregation
 *
 * IMPORTANT: This hook handles member modifications. For reading
 * member data, use the useMerchantMembers aggregator hook.
 *
 * NOTE: inviteMember has been REMOVED from the codebase.
 * Members now join via self-registration using the merchant registration link (Story 3.6).
 * See: getMerchantBySlug for the public endpoint that supports this flow.
 *
 * NOTE: Remove member endpoint is not yet implemented in the API.
 * When it becomes available, add useRemoveMember.
 */

// ============================================================================
// TYPES
// ============================================================================

import {
  useCreateMemberEnrollment,
  useCreateMerchantGroup,
  useUpdateMerchantGroup,
  useCreateMerchantGroupMemberAssignment,
  useRemoveMerchantGroupMember,
} from '@/generated/hooks';
import type {
  CreateMemberEnrollmentBodyType,
  CreateMemberEnrollmentResponseType,
  CreateMerchantGroupBodyType,
  CreateMerchantGroupResponseType,
  UpdateMerchantGroupBodyType,
  UpdateMerchantGroupResponseType,
  CreateMerchantGroupMemberAssignmentResponseType,
} from '@zlinkx/shared-v2';

/**
 * Options for useMerchantMemberActions hook
 */
type UseMerchantMemberActionsOptions = Record<string, never>;

/**
 * Loading states for actions
 */
type MerchantMemberActionsLoading = {
  enrollMember: boolean;
  createGroup: boolean;
  updateGroup: boolean;
  assignGroupMember: boolean;
  removeGroupMember: boolean;
};

/**
 * Return type for useMerchantMemberActions hook
 */
interface UseMerchantMemberActionsReturn {
	/**
	 * Loading states per action
	 */
	isLoading: MerchantMemberActionsLoading;
	enrollMember: (
		merchantId: number,
		data: CreateMemberEnrollmentBodyType,
	) => Promise<CreateMemberEnrollmentResponseType['data']>;
	createGroup: (
		merchantId: number,
		data: CreateMerchantGroupBodyType,
	) => Promise<CreateMerchantGroupResponseType['data']>;
	updateGroup: (
		merchantId: number,
		cardId: string,
		data: UpdateMerchantGroupBodyType,
	) => Promise<UpdateMerchantGroupResponseType['data']>;
	assignGroupMember: (
		merchantId: number,
		cardId: string,
		userId: string,
	) => Promise<CreateMerchantGroupMemberAssignmentResponseType['data']>;
	removeGroupMember: (
		merchantId: number,
		cardId: string,
		userId: string,
	) => Promise<void>;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Coordinates merchant member actions
 *
 * NOTE: inviteMember has been REMOVED. Members join via self-registration.
 *
 * @example
 * ```tsx
 * function MemberManagementPage() {
 *   const actions = useMerchantMemberActions();
 *
 *   // Currently no actions available
 *   // Future: member removal, role updates, etc.
 *
 *   return <MemberList />;
 * }
 * ```
 */
export function useMerchantMemberActions(
	options?: UseMerchantMemberActionsOptions,
): UseMerchantMemberActionsReturn {
	void options;
	const enrollmentMutation = useCreateMemberEnrollment();
	const createGroupMutation = useCreateMerchantGroup();
	const updateGroupMutation = useUpdateMerchantGroup();
	const assignGroupMemberMutation = useCreateMerchantGroupMemberAssignment();
	const removeGroupMemberMutation = useRemoveMerchantGroupMember();

	const enrollMember = async (
		merchantId: number,
		data: CreateMemberEnrollmentBodyType,
	): Promise<CreateMemberEnrollmentResponseType['data']> =>
		 enrollmentMutation.mutateAsync({ merchantId, data });
	const createGroup = async (
		merchantId: number,
		data: CreateMerchantGroupBodyType,
	): Promise<CreateMerchantGroupResponseType['data']> =>
		createGroupMutation.mutateAsync({ merchantId, data });
	const updateGroup = async (
		merchantId: number,
		cardId: string,
		data: UpdateMerchantGroupBodyType,
	): Promise<UpdateMerchantGroupResponseType['data']> =>
		updateGroupMutation.mutateAsync({ merchantId, cardId, data });
	const assignGroupMember = async (
		merchantId: number,
		cardId: string,
		userId: string,
	): Promise<CreateMerchantGroupMemberAssignmentResponseType['data']> =>
		assignGroupMemberMutation.mutateAsync({
			merchantId,
			cardId,
			data: { user_id: userId },
		});
	const removeGroupMember = async (
		merchantId: number,
		cardId: string,
		userId: string,
	): Promise<void> => {
		await removeGroupMemberMutation.mutateAsync({ merchantId, cardId, userId });
	};

	// ========================================
	// Return Coordinator Pattern
	// ========================================
	return {
		isLoading: {
			enrollMember: enrollmentMutation.isPending,
			createGroup: createGroupMutation.isPending,
			updateGroup: updateGroupMutation.isPending,
			assignGroupMember: assignGroupMemberMutation.isPending,
			removeGroupMember: removeGroupMemberMutation.isPending,
		},
		enrollMember,
		createGroup,
		updateGroup,
		assignGroupMember,
		removeGroupMember,
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantMemberActions;

export type {
	UseMerchantMemberActionsOptions,
	MerchantMemberActionsLoading,
	UseMerchantMemberActionsReturn,
};
